import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Flex, HStack, Text, Spinner, Button, CloseButton, Icon } from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import API from '../helpers/axiosInstance';
import { downloadCsvFile } from '../helpers/csvExport';

/**
 * CSV exports run as an async backend job (/msg-log/export-data returns
 * `processing` + a status_url to poll, not the file itself) and can take
 * minutes for large logs. Tracking that ONLY in component state meant a page
 * reload — or just navigating away — silently threw the whole export away:
 * the polling loop lived in a local `for` loop that died with the component.
 *
 * This keeps the one active job in localStorage, so a fresh page load (any
 * page, not just the one that started it) picks the poll back up instead of
 * losing it, and shows it in a persistent top banner instead of a toast that
 * disappears — a job that outlives a page reload needs status that does too.
 */
const STORAGE_KEY = 'xnotify_csv_export_job';
const POLL_INTERVAL_MS = 3000;

const getHeaders = () => ({
    'x-api-key': localStorage.getItem('api-key'),
    'x-access-token': localStorage.getItem('customer-portal'),
});

const readStoredJob = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/** True for the rejection an AbortController.abort() itself produces — not a real failure. */
const isCancelError = (error) => error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

const CsvExportContext = createContext(null);

export function CsvExportProvider({ children }) {
    const [job, setJob] = useState(null); // { label, statusUrl, filename, phase: 'polling'|'downloading', downloadUrl? }
    const [result, setResult] = useState(null); // { status, message, rows_written, truncated }
    const pollTimeoutRef = useRef(null);
    // Whichever network request is currently in flight (the status poll, or
    // the file transfer itself) — cancelExport aborts THIS, not just the timer.
    const abortControllerRef = useRef(null);

    const newSignal = () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return controller.signal;
    };

    const persistJob = (j) => {
        if (j) localStorage.setItem(STORAGE_KEY, JSON.stringify(j));
        else localStorage.removeItem(STORAGE_KEY);
    };

    const stopPolling = () => {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
    };

    const finishJob = useCallback(() => {
        setJob(null);
        persistJob(null);
        stopPolling();
    }, []);

    /**
     * The actual file transfer (fetching potentially hundreds of thousands of
     * rows as a blob) can itself take a while — a 200 on the download request
     * only means the response STARTED, not that the bytes are in yet. A reload
     * mid-transfer used to kill it same as a reload mid-poll did, with no way
     * back since the job was already cleared the moment 'completed' was seen.
     *
     * So the job stays in localStorage (phase: 'downloading', with the
     * download_url/filename now known) until the transfer itself finishes —
     * a reload here just re-issues the same GET rather than losing the export
     * entirely and forcing the whole backend job to be redone from scratch.
     */
    const runDownload = useCallback(async (currentJob, downloadUrl, filename, extra) => {
        const downloadingJob = { ...currentJob, phase: 'downloading', downloadUrl, filename };
        setJob(downloadingJob);
        persistJob(downloadingJob);
        // A 200 on the download request only means the transfer STARTED — for a
        // large export the bytes themselves can take a while, so this is its
        // own visible phase rather than still showing "ready" while the file
        // hasn't actually reached the browser yet.
        setResult({ status: 'downloading', message: 'Downloading your CSV file…', ...extra });
        try {
            await downloadCsvFile({ downloadUrl, filename, headers: getHeaders(), signal: newSignal() });
            setResult({ status: 'completed', message: 'Your CSV has downloaded.', ...extra });
            // Auto-clear the "ready" banner after a few seconds — but only if
            // it's still showing THIS success (the user may have already
            // started a new export by the time this timer fires).
            setTimeout(() => setResult(prev => (prev?.status === 'completed' ? null : prev)), 5000);
        } finally {
            finishJob();
        }
    }, [finishJob]);

    const poll = useCallback((statusUrl, currentJob) => {
        (async () => {
            try {
                const res = await API.get(statusUrl, { headers: getHeaders(), signal: newSignal() });
                const data = res.data;

                if (data?.status === 'completed') {
                    await runDownload(currentJob, data.download_url, data.filename || currentJob.filename, {
                        truncated: data.truncated, rows_written: data.rows_written,
                    });
                    return;
                }
                if (data?.status === 'failed' || data?.status === 'error') {
                    setResult({ status: 'failed', message: data?.message || 'The export failed.' });
                    finishJob();
                    return;
                }

                setResult({ status: 'processing', message: 'Your CSV is being processed — this can take a moment for large exports.' });
                pollTimeoutRef.current = setTimeout(() => poll(statusUrl, currentJob), POLL_INTERVAL_MS);
            } catch (error) {
                if (isCancelError(error)) return; // cancelExport already cleared everything
                setResult({ status: 'failed', message: error.message || 'Something went wrong while exporting.' });
                finishJob();
            }
        })();
    }, [finishJob, runDownload]);

    // Resume whatever was mid-flight on a fresh load of ANY page — this is
    // what survives the reload the polling loop (and the download fetch
    // itself) can't.
    useEffect(() => {
        const saved = readStoredJob();
        if (saved?.phase === 'downloading' && saved.downloadUrl) {
            setJob(saved);
            runDownload(saved, saved.downloadUrl, saved.filename).catch(error => {
                if (isCancelError(error)) return;
                setResult({ status: 'failed', message: error.message || 'Something went wrong resuming the download.' });
            });
        } else if (saved?.statusUrl) {
            setJob(saved);
            setResult({ status: 'processing', message: 'Resuming your export…' });
            poll(saved.statusUrl, saved);
        }
        return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startExport = useCallback(async ({ exportUrl, label, filenameFallback }) => {
        stopPolling();
        setResult({ status: 'processing', message: 'Starting your export…' });
        try {
            const initial = await API.get(exportUrl, { headers: getHeaders(), signal: newSignal() });
            const data = initial.data;
            const baseJob = { label: label || 'CSV export', filename: filenameFallback };

            if (data?.status === 'completed') {
                await runDownload(baseJob, data.download_url, data.filename || filenameFallback, {
                    truncated: data.truncated, rows_written: data.rows_written,
                });
                return;
            }
            if (data?.status !== 'processing') {
                setResult({ status: 'failed', message: data?.message || 'Unexpected response starting the export.' });
                return;
            }

            const statusPath = data.status_url || '/msg-log/export-data/status';
            const statusUrl = `${process.env.REACT_APP_API_URL}${statusPath}`;
            const newJob = { ...baseJob, phase: 'polling', statusUrl };
            setJob(newJob);
            persistJob(newJob);
            poll(statusUrl, newJob);
        } catch (error) {
            if (isCancelError(error)) return;
            setResult({ status: 'failed', message: error.message || 'Something went wrong starting the export.' });
        }
    }, [poll, runDownload]);

    const dismiss = useCallback(() => setResult(null), []);

    /** Stops whichever request is in flight (poll or file transfer) and drops the job entirely. */
    const cancelExport = useCallback(() => {
        abortControllerRef.current?.abort();
        stopPolling();
        finishJob();
        setResult(null);
    }, [finishJob]);

    return (
        <CsvExportContext.Provider value={{
            startExport, cancelExport, activeJob: job, exportStatus: result, dismissExportStatus: dismiss,
            // `job` (not `result`) is the source of truth: dismissing the banner
            // clears `result` but the export keeps running in the background, so
            // the Export button must stay disabled until the job itself is gone.
            isExporting: !!job,
        }}>
            {children}
        </CsvExportContext.Provider>
    );
}

/**
 * Self-contained — reads the active job straight from context, so any
 * component can just render `<CsvExportBanner />` wherever it wants the strip
 * to appear (BoxWrapper, so it shows below the page header/nav rather than
 * above it) without threading props through.
 */
export function CsvExportBanner() {
    const { exportStatus: result, activeJob, dismissExportStatus: onDismiss, cancelExport } = useCsvExport();
    if (!result) return null;

    const isSpinning = result.status === 'processing' || result.status === 'downloading';
    const isFailed = result.status === 'failed' || result.status === 'error';
    const isCompleted = result.status === 'completed';

    const palette = isFailed
        ? { bg: '#fef2f2', border: '#fecaca', title: 'CSV export failed' }
        : isCompleted
            ? { bg: '#f0fdf4', border: '#bbf7d0', title: 'CSV export ready' }
            : result.status === 'downloading'
                ? { bg: 'rgb(var(--brand-primary-rgb) / 0.08)', border: 'rgb(var(--brand-primary-rgb) / 0.25)', title: 'Downloading CSV' }
                : { bg: 'rgb(var(--brand-primary-rgb) / 0.08)', border: 'rgb(var(--brand-primary-rgb) / 0.25)', title: activeJob?.label || 'Exporting CSV' };

    return (
        <Box
            mb={4} borderRadius="10px"
            bg={palette.bg} border="1px solid" borderColor={palette.border}
            px={4} py={2}
        >
            <Flex align="center" justify="space-between" gap={3}>
                <HStack spacing={3} minW="0">
                    {isSpinning && <Spinner size="sm" color="var(--brand-primary)" thickness="2px" flexShrink={0} />}
                    {isFailed && <Icon as={FiXCircle} color="red.500" boxSize={4} flexShrink={0} />}
                    {isCompleted && <Icon as={FiCheckCircle} color="green.500" boxSize={4} flexShrink={0} />}
                    <Text fontSize="13px" fontWeight="700" color="gray.800" whiteSpace="nowrap">{palette.title}</Text>
                    <Text fontSize="12.5px" color="gray.600" noOfLines={1}>
                        {result.message}
                        {result.truncated ? ` — only the first ${Number(result.rows_written || 0).toLocaleString()} rows were included.` : ''}
                    </Text>
                </HStack>
                <HStack spacing={2} flexShrink={0}>
                    {isSpinning && (
                        <Button size="xs" variant="outline" colorScheme="red" onClick={cancelExport}>
                            Cancel
                        </Button>
                    )}
                    <CloseButton size="sm" onClick={onDismiss} />
                </HStack>
            </Flex>
        </Box>
    );
}

export function useCsvExport() {
    const ctx = useContext(CsvExportContext);
    if (!ctx) throw new Error('useCsvExport must be used within a CsvExportProvider');
    return ctx;
}
